import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";
import PaymentDebitReview from "./PaymentDebitReview";
import PaymentMovementPanel from "./PaymentMovementPanel";
const field = "mt-1 block w-full rounded-lg border p-2",
  button = "rounded-lg border px-3 py-2 disabled:opacity-40";
export default function PaymentDebitEvidence({ companyId }) {
  const { accessToken, user } = useAuth(),
    canWrite = hasPermission(user, "accounting.vouchers_post");
  const [state, setState] = useState(null),
    [payments, setPayments] = useState([]),
    [payment, setPayment] = useState(null),
    [channel, setChannel] = useState(""),
    [debits, setDebits] = useState([]),
    [preview, setPreview] = useState(null),
    [confirmed, setConfirmed] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const generation = useRef(0),
    working = useRef(false),
    key = useRef(null);
  useEffect(() => {
    generation.current++;
    working.current = false;
    key.current = null;
    setState(null);
    setPayments([]);
    setPayment(null);
    setChannel("");
    setDebits([]);
    setPreview(null);
    setBusy(false);
    setError("");
    setNotice("");
    return () => {
      generation.current++;
    };
  }, [companyId, accessToken]);
  async function run(fn, done) {
    if (working.current) return;
    working.current = true;
    const g = ++generation.current;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const r = await fn();
      if (g === generation.current) done(r);
    } catch (e) {
      if (g === generation.current) setError(e.message);
    } finally {
      if (g === generation.current) {
        working.current = false;
        setBusy(false);
      }
    }
  }
  const root = `/accounting/${companyId}/payment-debits`;
  const request = (url, options = {}) =>
    apiRequest(url, { accessToken, ...options });
  function clear() {
    setPreview(null);
    setConfirmed(false);
  }
  function save() {
    if (!preview || !confirmed || !canWrite || busy) return;
    const signature = JSON.stringify(preview.command);
    if (key.current?.signature !== signature) {
      if (!window.crypto?.randomUUID) {
        setError("HTTPS or localhost is required for safe request keys");
        return;
      }
      key.current = { signature, id: window.crypto.randomUUID() };
    }
    const body = {
      ...preview.command,
      previewHash: preview.previewHash,
      requestKey: key.current.id,
      originalPaymentReviewed: true,
      externalDebitReviewed: true,
      evidenceOnlyAccepted: true,
    };
    run(
      async () => {
        await request(root, { method: "POST", body });
        return request(root);
      },
      (r) => {
        setState((s) => ({ ...s, history: r }));
        clear();
        setNotice(
          "External debit evidence retained. No refund, financial voucher, stock or GST entry was created.",
        );
      },
    );
  }
  return (
    <section className="space-y-4 rounded-2xl border bg-white p-5">
      <header>
        <h2 className="text-xl font-semibold">
          Refund and chargeback debit evidence
        </h2>
        <p className="mt-2 text-slate-600">
          Record an already observed external debit against its original posted
          POS card/UPI payment. This does not execute a refund or post
          accounting. Bank charges, aggregated deductions and returned/voided
          sales need separate review.
        </p>
      </header>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      <button
        className={button}
        disabled={busy}
        onClick={() =>
          run(
            () =>
              Promise.all([
                request(root),
                request(`/accounting/${companyId}/settlement-evidence`),
              ]),
            ([history, settlements]) =>
              setState({ history, channels: settlements.channels }),
          )
        }
      >
        Load external debit workspace
      </button>
      {state && (
        <>
          {canWrite && (
            <fieldset disabled={busy} className="space-y-4">
              <form
                className="flex items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  const q = new FormData(e.currentTarget).get("q");
                  clear();
                  setPayment(null);
                  run(
                    () =>
                      request(`${root}/payments?q=${encodeURIComponent(q)}`),
                    (r) => setPayments(r.rows),
                  );
                }}
              >
                <label className="grow">
                  Original receipt search
                  <input
                    className={field}
                    required
                    minLength={2}
                    maxLength={100}
                    name="q"
                  />
                </label>
                <button className={button}>Find original payment</button>
              </form>
              <label className="block">
                Original payment
                <select
                  className={field}
                  value={payment?.paymentId || ""}
                  onChange={(e) => {
                    clear();
                    setPayment(
                      payments.find((p) => p.paymentId === e.target.value) ||
                        null,
                    );
                  }}
                >
                  <option value="">Select original receipt and payment</option>
                  {payments.map((p) => (
                    <option key={p.paymentId} value={p.paymentId}>
                      {p.receiptNumber} · {p.method} · INR {p.amount} ·{" "}
                      {p.reference || "No recorded reference"}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                Original collection channel
                <select
                  className={field}
                  value={channel}
                  onChange={(e) => {
                    clear();
                    setChannel(e.target.value);
                    setDebits([]);
                  }}
                >
                  <option value="">Select reviewed channel</option>
                  {state.channels.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} · {c.kind} · {c.method}
                      {c.retiredId ? " · retired history" : ""}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className={button}
                disabled={!channel}
                onClick={() =>
                  run(
                    () =>
                      request(
                        `${root}/bank-debits?channelId=${encodeURIComponent(channel)}`,
                      ),
                    (r) => setDebits(r.rows),
                  )
                }
              >
                Load retained bank debits
              </button>
              <form
                className="grid gap-3 md:grid-cols-2"
                onChange={clear}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!payment || !channel) return;
                  const v = Object.fromEntries(new FormData(e.currentTarget)),
                    command = {
                      ...v,
                      sourceId: payment.sourceId,
                      paymentId: payment.paymentId,
                      channelId: channel,
                    };
                  run(
                    () =>
                      request(`${root}/preview`, {
                        method: "POST",
                        body: command,
                      }),
                    (r) => setPreview({ ...r, command }),
                  );
                }}
              >
                <label>
                  Exact retained bank debit
                  <select className={field} required name="bankRowId">
                    <option value="">
                      Select one debit (latest 100 shown)
                    </option>
                    {debits.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.date.slice(0, 10)} · INR {r.debit} ·{" "}
                        {r.reference || r.description}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Debit classification
                  <select className={field} name="kind">
                    <option value="REFUND_DEBIT">Customer refund debit</option>
                    <option value="CHARGEBACK_DEBIT">Chargeback debit</option>
                  </select>
                </label>
                <label>
                  Amount (INR)
                  <input
                    className={field}
                    required
                    name="amount"
                    inputMode="decimal"
                    pattern="[0-9]+(\.[0-9]{1,2})?"
                  />
                </label>
                <label>
                  External refund / case reference
                  <input
                    className={field}
                    required
                    name="externalReference"
                    minLength={3}
                    maxLength={150}
                  />
                </label>
                <label className="md:col-span-2">
                  Payment and bank comparison note
                  <textarea
                    className={field}
                    required
                    name="reviewNote"
                    minLength={20}
                    maxLength={1000}
                  />
                </label>
                <button className={button} disabled={!payment || !channel}>
                  Preview external debit evidence
                </button>
              </form>
              {preview && (
                <div className="space-y-3 rounded-lg bg-amber-50 p-4">
                  <p>{preview.warning}</p>
                  <dl>
                    <dt>Original payment</dt>
                    <dd>INR {preview.payload.originalPaymentAmount}</dd>
                    <dt>Previously retained refund and chargeback debits</dt>
                    <dd>INR {preview.payload.previouslyRecorded}</dd>
                    <dt>Remaining after this record</dt>
                    <dd>INR {preview.payload.remainingAfter}</dd>
                  </dl>
                  <label className="block">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                    />{" "}
                    I reviewed the original payment, external case reference and
                    exact bank debit. I accept this retains evidence only and is
                    not a completed refund or accounting correction.
                  </label>
                  <button
                    className={button}
                    disabled={!confirmed}
                    onClick={save}
                  >
                    Retain reviewed debit evidence
                  </button>
                </div>
              )}
            </fieldset>
          )}
          <h3 className="font-semibold">
            Retained external debits (latest 100)
          </h3>
          {!state.history.rows.length ? (
            <p>No external debit evidence retained.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr>
                    {[
                      "Bank date",
                      "Receipt",
                      "Classification",
                      "Amount INR",
                      "Reference",
                      "Accounting status",
                      "Review / history",
                    ].map((s) => (
                      <th className="p-2" key={s}>
                        {s}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {state.history.rows.map((r) => (
                    <tr key={r.id}>
                      <td className="p-2">{r.snapshot.bankDate}</td>
                      <td className="p-2">{r.snapshot.receiptNumber}</td>
                      <td className="p-2">
                        {r.currentKind || r.kind}
                        {r.voidedId ? " · voided" : ""}
                      </td>
                      <td className="p-2">{r.amount}</td>
                      <td className="p-2">{r.externalReference}</td>
                      <td className="p-2">
                        {r.voidedId
                          ? "Voided evidence · history retained"
                          : r.financialStatus ===
                              "POSTED_TO_UNRESOLVED_SUSPENSE"
                            ? "Posted to suspense · resolution required"
                            : "Evidence only · financial review required"}
                      </td>
                      <td className="p-2">
                        <PaymentMovementPanel
                          companyId={companyId}
                          debit={r}
                          onUpdated={async () => {
                            const history = await request(root);
                            setState((s) => ({ ...s, history }));
                          }}
                        />
                        <PaymentDebitReview
                          key={
                            r.id +
                            ":" +
                            (r.events?.length || 0) +
                            ":" +
                            r.voidedId
                          }
                          companyId={companyId}
                          debit={r}
                          onUpdated={async () => {
                            const history = await request(root);
                            setState((s) => ({ ...s, history }));
                            setNotice(
                              "Reviewed event retained with original history. No financial posting or money movement.",
                            );
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
